import React, { useCallback, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { Box } from "@mui/material";
import { useSecurityImages } from "@hooks";
import { useInvestmentsId } from "@rentablo/api/apiv1";
import { SecurityImage } from "@rentablo/components";
import { InvestmentDetailModal } from "@features/investments/investment/components/InvestmentTable/InvestmentDetailModal";
import { ClassificationChartLegend } from "./legend";
import { ClassificationDonutChart } from "./ClassificationDonutChart";
import { useClassificationDrillDown } from "./useClassificationDrillDown";
import { ClassificationBreakdownData, ClassificationChartProps } from "./types";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(5)};
  width: 100%;

  ${({ theme }) => theme.breakpoints.up("md")} {
    display: grid;
    grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
    column-gap: ${({ theme }) => theme.spacing(17)};
    align-items: start;
  }
`;

export const ClassificationChart = ({
  breakdowns,
  isFetching = false,
  legendSectionTitle,
  legendTitleTooltipKey,
  renderLegendBeforeEl,
  actions,
}: ClassificationChartProps) => {
  const [modalInvestmentId, setModalInvestmentId] = useState<
    number | undefined
  >();
  const { data: modalInvestment } = useInvestmentsId(
    { id: modalInvestmentId! },
    { enabled: modalInvestmentId !== undefined }
  );

  const {
    donutData,
    hoveredItem,
    currentTotalValue,
    mainBreakdown,
    rootTotalValue,
    breadcrumbs,
    legendChartTitle,
    legendItems,
    handleSliceClick,
    handleLegendClick,
    handleHover,
  } = useClassificationDrillDown(breakdowns, legendSectionTitle);

  const leafSecurities = useMemo(
    () =>
      breakdowns
        .filter(
          (b) =>
            !b.hasChildren &&
            (b.isin ? !b.isin.startsWith("_NA_") : Boolean(b.tickerSymbol))
        )
        .map((b) => ({
          isin: b.isin,
          tickerSymbol: b.tickerSymbol,
          quoteProvider: b.quoteProvider,
          hasValidIsin: Boolean(b.isin),
          type: b.investmentType,
        })),
    [breakdowns]
  );
  const images = useSecurityImages(leafSecurities);

  const combinedBeforeEl = useCallback(
    (item: ClassificationBreakdownData) => {
      const custom = renderLegendBeforeEl?.(item);

      if (item.isin || item.tickerSymbol) {
        const src = images[item.isin || ""] || images[item.tickerSymbol || ""];
        return (
          <Box display="flex" alignItems="center" gap={2}>
            <SecurityImage src={src} width={24} height={24} />
            {custom}
          </Box>
        );
      }

      return custom;
    },
    [renderLegendBeforeEl, images]
  );

  const handleDonutSliceClick = useCallback(
    (item: ClassificationBreakdownData) => {
      handleSliceClick(item);
      if (!item.hasChildren && item.id != null) {
        setModalInvestmentId(item.id);
      }
    },
    [handleSliceClick]
  );

  const handleInvestmentClick = useCallback(
    (item: ClassificationBreakdownData) => {
      if (item.id != null) {
        setModalInvestmentId(item.id);
      }
    },
    []
  );

  const handleModalClose = useCallback(() => {
    setModalInvestmentId(undefined);
  }, []);

  if (!breakdowns.length && !isFetching) {
    return null;
  }

  return (
    <Container>
      <Box sx={{ width: "100%" }}>
        <ClassificationDonutChart
          data={donutData}
          ringTotalValue={hoveredItem?.value ?? currentTotalValue}
          centerSubtitle={hoveredItem?.name ?? mainBreakdown?.name ?? ""}
          isLoading={isFetching}
          hoveredItem={hoveredItem}
          onSectionClick={handleDonutSliceClick}
          onSectionHover={handleHover}
        />
      </Box>

      <Box>
        {actions}
        <ClassificationChartLegend
          breadcrumbs={breadcrumbs}
          chartTitle={legendChartTitle}
          chartTitleTooltipKey={legendTitleTooltipKey}
          items={legendItems}
          rootTotalValue={rootTotalValue}
          isLoading={isFetching}
          hoveredItem={hoveredItem}
          renderBeforeEl={combinedBeforeEl}
          onItemHover={handleHover}
          onItemClick={handleLegendClick}
          onInvestmentClick={handleInvestmentClick}
        />
      </Box>
      <InvestmentDetailModal
        investment={modalInvestment}
        isOpen={modalInvestmentId !== undefined}
        onClose={handleModalClose}
        image={
          modalInvestment
            ? images[modalInvestment.isin || ""] ||
              images[modalInvestment.tickerSymbol || ""]
            : undefined
        }
      />
    </Container>
  );
};
